import { useTranslation } from 'react-i18next';
import { HpButton, HpTable, HpTag, TableScroll } from '@hp/ui';
import { useMe } from '../../api/me';
import { PdLink } from '../../components/pd-link';
import { RelativeTime } from '../../components/relative-time';
import { BrutTheadRow } from '../../components/ui/table';
import { actionFor, durationParts, type LeadAction } from './labels';
import { LeadStatusTag } from './lead-detail';
import type { ReceivedLead } from './queries';

/** "12 s" / "3 min" / "1.5 h" for a delivery time in whole seconds; a dash when not delivered. */
function DeliveryTime({ seconds }: { seconds: number | null }) {
  const { t } = useTranslation(['leads']);
  if (seconds === null) return <span className="bo-muted">—</span>;
  const { unit, value } = durationParts(seconds);
  return <span className="bo-mono">{t(`received.duration.${unit}`, { value })}</span>;
}

/** One received lead per row; a row click (or Enter) opens its detail above the table. */
export function ReceivedTable({
  rows,
  canManage,
  selectedId,
  busyId,
  empty,
  onSelect,
  onAction,
}: {
  rows: ReceivedLead[];
  canManage: boolean;
  selectedId: string | null;
  /** The lead whose retry / re-route is running. */
  busyId: string | null;
  /** The text of the empty row. */
  empty: string;
  onSelect: (id: string) => void;
  onAction: (lead: ReceivedLead, action: LeadAction) => void;
}) {
  const { t } = useTranslation(['leads', 'common']);
  const pdDomain = useMe().data?.pipedriveCompanyDomain;
  const columns = canManage ? 10 : 9;

  return (
    <TableScroll label={t('received.title')}>
      {/* A grid: rows are focusable and selectable (Enter opens the detail), so `aria-selected` is announced. */}
      <HpTable role="grid" aria-label={t('received.title')} aria-readonly>
        <BrutTheadRow>
          <th>{t('received.columns.received')}</th>
          <th>{t('received.columns.status')}</th>
          <th>{t('received.columns.channel')}</th>
          <th>{t('received.columns.contact')}</th>
          <th>{t('received.columns.campaign')}</th>
          <th>{t('received.columns.owner')}</th>
          <th>{t('received.columns.target')}</th>
          <th className="bo-num">{t('received.columns.delivery')}</th>
          <th>{t('received.columns.deal')}</th>
          {canManage && <th>{t('received.columns.actions')}</th>}
        </BrutTheadRow>
        <tbody>
          {rows.map((lead) => {
            const selected = lead.id === selectedId;
            const action = canManage ? actionFor(lead.status) : null;
            const contact = [lead.email, lead.phone].filter(Boolean).join(' · ');
            return (
              <tr
                key={lead.id}
                className={selected ? 'bo-row--link bo-row--expanded' : 'bo-row--link'}
                title={t('received.rowTitle')}
                tabIndex={0}
                aria-selected={selected}
                onClick={() => onSelect(lead.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target === e.currentTarget) onSelect(lead.id);
                }}
              >
                <td className="bo-nowrap">
                  <RelativeTime value={lead.receivedAt} />
                </td>
                <td>
                  <span className="bo-chips">
                    <LeadStatusTag status={lead.status} />
                    {lead.routedByDefault && (
                      <HpTag tone="warning" title={t('received.defaultTitle')}>
                        {t('received.defaultChip')}
                      </HpTag>
                    )}
                    {lead.isTest && (
                      <HpTag tone="info" title={t('received.testTitle')}>
                        {t('received.testChip')}
                      </HpTag>
                    )}
                  </span>
                </td>
                <td className="bo-nowrap">{lead.channelLabel}</td>
                <td>
                  <span className="bo-strong">{lead.name ?? t('received.unnamed')}</span>
                  {contact && (
                    <>
                      <br />
                      <small className="bo-muted">{contact}</small>
                    </>
                  )}
                </td>
                <td>{lead.campaignName ?? <span className="bo-muted">—</span>}</td>
                <td>
                  {lead.ownerUserPdId !== null ? (
                    (lead.ownerName ?? t('rules.unknownId', { id: lead.ownerUserPdId }))
                  ) : (
                    <span className="bo-muted">—</span>
                  )}
                </td>
                <td>
                  {lead.pipelineName ? (
                    <span>
                      {lead.pipelineName}
                      {lead.stageName && ` → ${lead.stageName}`}
                    </span>
                  ) : (
                    <span className="bo-muted">—</span>
                  )}
                </td>
                <td className="bo-num">
                  <DeliveryTime seconds={lead.secondsToDelivery} />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  {lead.dealPdId !== null ? (
                    <PdLink domain={pdDomain} entity="deal" pdId={lead.dealPdId} />
                  ) : (
                    <span className="bo-muted">—</span>
                  )}
                </td>
                {canManage && (
                  <td onClick={(e) => e.stopPropagation()}>
                    {action && (
                      <HpButton
                        variant="quiet"
                        icon={action === 'retry' ? 'refresh' : 'rotateCcw'}
                        className="bo-button--sm"
                        busy={busyId === lead.id}
                        disabled={busyId !== null}
                        onClick={() => onAction(lead, action)}
                      >
                        {t(`received.${action}`)}
                      </HpButton>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns} className="bo-cell-empty">
                {empty}
              </td>
            </tr>
          )}
        </tbody>
      </HpTable>
    </TableScroll>
  );
}
